import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useMelwaysLink } from '@seek/melways-react';
import { CountryCallingCodeDialog } from '@seek/profile-read-ui';
import { required, rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Box,
  Button,
  Column,
  Columns,
  Inline,
  Stack,
  Text,
  TextLink,
  Tiles,
} from 'braid-design-system';
import { kebabCase } from 'lodash';
import { useEffect } from 'react';
import { type FieldValues, type SubmitHandler, useForm } from 'react-hook-form';

import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import FormTextField from 'src/forms/common/FormTextField';
import { seekResolver } from 'src/forms/seekResolver';
import useOnMountFocus from 'src/forms/use-onmount-focus';
import { useConfirm } from 'src/modules/imperative-confirm';

import HomeLocationField from './HomeLocationField';
import translations from './PersonalDetails.vocab';
import PhoneField from './PhoneField';
import { type DefaultValues, ErrorKey } from './types';

interface Props<T extends FieldValues> {
  email?: string;
  defaultValues?: T;
  onSubmit?: (data: T) => void;
  onCancel?: () => void;
  errors?: ErrorKey[];
  onFormDirtyChange?: (isDirty: boolean) => void;
  verifiedIdentityName?: string;
}

const defaultDefaultValues: DefaultValues = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  countryCodeId: '',
  homeLocation: { text: '', value: '' },
};

const PersonalDetailsForm = ({
  defaultValues = defaultDefaultValues,
  email = '',
  onSubmit,
  onCancel,
  errors = [],
  onFormDirtyChange,
  verifiedIdentityName,
}: Props<DefaultValues>) => {
  const { t } = useTranslations(translations);
  const settingsLink = useMelwaysLink('/settings');
  const phone = rules.phoneNumber();

  const schema = {
    firstName: rules.name(),
    lastName: rules.name(),
    phoneNumber: {
      validators: [required, ...phone.validators],
      validatorProps: {
        requiredLabel: undefined,
        ...phone.validatorProps,
      },
    },
    countryCodeId: { validators: [required] },
    homeLocation: {
      ...rules.granularCurrentLocation({
        dataLabel: t('Location'),
      }),
    },
  };

  const renderErrorMsg = (key: ErrorKey) => {
    const errorMessageMap = {
      [ErrorKey.UNSAVED]: t('Please complete your personal details'),
      [ErrorKey.NETWORK]: t('Your personal details could not be saved'),
    };

    return errorMessageMap[key] ?? t('Please complete your personal details');
  };

  const trackingClient = useTrackingClient();

  const sendAnalytic = (fieldName: string) => {
    trackingClient.trackLink({
      link: 'apply_field_pressed',
      formField: fieldName,
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.PERSONAL_DETAILS,
    });
  };

  const { control, handleSubmit, formState } = useForm<DefaultValues>({
    mode: 'onTouched',
    defaultValues,
    resolver: seekResolver(schema),
  });

  useEffect(() => {
    if (onFormDirtyChange) {
      onFormDirtyChange(formState.isDirty);
    }
  }, [onFormDirtyChange, formState.isDirty]);

  const { isSubmitting } = formState;
  useEffect(() => {
    const fieldNames = Object.keys(formState.errors).map((k) => kebabCase(k));

    if (fieldNames.length > 0 && isSubmitting) {
      trackingClient.trackLink({
        link: 'apply-field-validation-failed',
        formField: fieldNames,
      });
    }
  }, [formState, trackingClient, isSubmitting]);

  const focusRef = useOnMountFocus();

  const shouldShowPhoneDialog =
    !defaultValues.countryCodeId && Boolean(defaultValues.phoneNumber);

  const [modalNode, openModal] = useConfirm();

  const proccessSubmit: SubmitHandler<DefaultValues> = async (data) => {
    const shouldAbort =
      shouldShowPhoneDialog &&
      (await openModal<boolean>((close) => (
        <CountryCallingCodeDialog
          open
          onSave={() => close(false)}
          onClose={() => close(true)}
          countryCallingCode={{ id: data.countryCodeId }}
          phoneNumber={data.phoneNumber}
        />
      )));

    if (!shouldAbort) {
      return onSubmit?.(data);
    }
  };

  const trackSettingsClick = () =>
    trackingClient.trackLink({
      link: 'account-settings-link-clicked',
    });

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(proccessSubmit)}>
        <Box component="fieldset">
          <Stack space="xlarge">
            <Stack space="xlarge">
              {shouldShowPhoneDialog && (
                <Alert tone="info">
                  <Text>
                    {t(
                      'Phone number will be updated to include a country code',
                    )}
                  </Text>
                </Alert>
              )}
              <Stack space="small">
                <Tiles
                  space={{ mobile: 'small', tablet: 'medium' }}
                  columns={{ mobile: 1, tablet: 2 }}
                >
                  <FormTextField
                    ref={focusRef}
                    control={control}
                    name="firstName"
                    label={t('First name')}
                    onFocus={() => sendAnalytic('first-name')}
                    reserveMessageSpace
                  />
                  <FormTextField
                    control={control}
                    name="lastName"
                    label={t('Last name')}
                    onFocus={() => sendAnalytic('last-name')}
                    reserveMessageSpace
                  />
                </Tiles>
                <HomeLocationField
                  control={control}
                  onFocus={() => sendAnalytic('home-location')}
                />
                <PhoneField
                  control={control}
                  onFocus={() => sendAnalytic('phone-number')}
                />
                <Stack space="medium">
                  <Text weight="strong">{t('Email address')}</Text>
                  <Columns space="small" collapseBelow="tablet">
                    <Column>
                      <Text>{email}</Text>
                    </Column>
                    <Column width="content">
                      <Text align={{ tablet: 'right' }}>
                        <TextLink
                          href={settingsLink}
                          key="settings"
                          hitArea="large"
                          onClick={trackSettingsClick}
                        >
                          {t('Edit in Settings')}
                        </TextLink>
                      </Text>
                    </Column>
                  </Columns>
                </Stack>
              </Stack>
              {Boolean(verifiedIdentityName) && (
                <Stack space="medium">
                  <Text weight="strong">{t('Verified identity title')}</Text>
                  <Stack space="small">
                    <Text>
                      <Inline space="xsmall" alignY="center">
                        <SeekPassIconVerified />
                        {<>{verifiedIdentityName}</>}
                      </Inline>
                    </Text>
                    <Text size="xsmall" tone="secondary">
                      {t('Verified identity desc')}
                    </Text>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Actions>
              <Button
                loading={isSubmitting}
                type="submit"
                tone="formAccent"
                data={{ testid: 'save-personal-details' }}
              >
                {t('Save')}
              </Button>
              {onCancel && !isSubmitting && (
                <Button
                  tone="formAccent"
                  variant="transparent"
                  onClick={onCancel}
                  data={{ testid: 'cancel-personal-details' }}
                >
                  {t('Cancel')}
                </Button>
              )}
            </Actions>
            {errors.map((message) => (
              <Alert key={message} tone="critical">
                <Text>{renderErrorMsg(message)}</Text>
              </Alert>
            ))}
          </Stack>
        </Box>
      </Box>
      {modalNode}
    </>
  );
};

export default PersonalDetailsForm;
